import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import {
  AssessmentTemplateDto,
  bffApi,
  CreateAssessmentTemplateRequestDto,
  useListAssessmentTemplatesQuery,
} from "../../autogen/bff-api";

interface AssessmentDimension {
  id: string;
  name: string;
  description: string;
}

interface AssessmentTemplateForm {
  id?: string;
  templateFamilyId?: string;
  name: string;
  description: string;
  dimensions: AssessmentDimension[];
}

export const AssessmentTemplates = () => {
  const { t } = useTranslation();
  const toast = useToast();

  // Using the AssessmentTemplateDto from the BFF API

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<AssessmentTemplateForm>({
    name: "",
    description: "",
    dimensions: [],
  });

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: templateData, isLoading } = useListAssessmentTemplatesQuery({});
  const [createTemplate, { isLoading: isCreating }] = bffApi.useCreateAssessmentTemplateMutation();

  const latestTemplates = useMemo(() => {
    if (!templateData?.templates?.length) return [];

    const templateGroups: Record<string, AssessmentTemplateDto[]> = {};
    templateData.templates.forEach((template) => {
      if (!templateGroups[template.templateId]) {
        templateGroups[template.templateId] = [];
      }
      templateGroups[template.templateId].push(template);
    });

    return Object.values(templateGroups).map((group) => {
      return [...group].sort((a, b) => {
        const versionA = a.templateVersion || 0;
        const versionB = b.templateVersion || 0;
        return versionB - versionA;
      })[0];
    });
  }, [templateData?.templates]);

  const handleAddTemplate = () => {
    setIsEditing(false);
    setFormData({
      name: "",
      description: "",
      dimensions: [],
    });
    onOpen();
  };

  const handleEditTemplate = (template: AssessmentTemplateDto) => {
    setIsEditing(true);
    // Map API response to form data
    setFormData({
      id: template.id,
      templateFamilyId: template.templateId, // Store the template family ID
      name: template.name,
      description: template.description || "",
      dimensions: template.dimensions.map((dim) => ({
        id: dim.id || `dim-${Date.now()}-${Math.random()}`,
        name: dim.name,
        description: dim.description || "",
      })),
    });
    onOpen();
  };

  const handleAddDimension = () => {
    setFormData({
      ...formData,
      dimensions: [...formData.dimensions, { id: `dim-${Date.now()}`, name: "", description: "" }],
    });
  };

  const handleDimensionChange = (index: number, field: keyof AssessmentDimension, value: string) => {
    const newDimensions = [...formData.dimensions];
    newDimensions[index] = {
      ...newDimensions[index],
      [field]: value,
    };
    setFormData({ ...formData, dimensions: newDimensions });
  };

  const handleRemoveDimension = (index: number) => {
    const newDimensions = formData.dimensions.filter((_, i) => i !== index);
    setFormData({ ...formData, dimensions: newDimensions });
  };

  const handleSubmit = async () => {
    try {
      const templateRequest: CreateAssessmentTemplateRequestDto = {
        name: formData.name,
        description: formData.description,
        dimensions: formData.dimensions.map((dim, index) => ({
          name: dim.name,
          description: dim.description,
          weight: 1,
          criteria: [],
          order: index + 1,
        })),
      };

      if (isEditing && formData.templateFamilyId) {
        templateRequest.templateFamilyId = formData.templateFamilyId;

        await createTemplate({ createAssessmentTemplateRequestDto: templateRequest }).unwrap();
        toast({
          title: t("Template Updated"),
          description: t("A new version of the assessment template has been created"),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        await createTemplate({ createAssessmentTemplateRequestDto: templateRequest }).unwrap();
        toast({
          title: t("Template Created"),
          description: t("Assessment template has been created successfully"),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      onClose();
    } catch (error) {
      console.error("Error submitting template:", error);
      toast({
        title: t("Error"),
        description: t("Failed to save the assessment template"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md">{t("Assessment Templates")}</Heading>
        <Button leftIcon={<FaPlus />} colorScheme="blue" onClick={handleAddTemplate}>
          {t("Add Template")}
        </Button>
      </Flex>

      {latestTemplates.length === 0 ? (
        <Text>
          {t("No assessment templates found")}. {t("Create your first template to start assessing organizations")}.
        </Text>
      ) : (
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>{t("Name")}</Th>
              <Th>{t("Description")}</Th>
              <Th>{t("Version")}</Th>
              <Th>{t("Dimensions")}</Th>
              <Th>{t("Actions")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {latestTemplates.map((template) => (
              <Tr key={template.id}>
                <Td>{template.name}</Td>
                <Td>{template.description}</Td>
                <Td>v{template.templateVersion || 1}</Td>
                <Td>{template.dimensions?.length || 0}</Td>
                <Td>
                  <Button size="sm" leftIcon={<FaEdit />} mr={2} onClick={() => handleEditTemplate(template)}>
                    {t("Edit")}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditing ? t("Edit Assessment Template") : t("Create Assessment Template")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>{t("Template Name")}</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={`${t("Template name")}...`}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("Description")}</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t("Describe the purpose of this assessment template") as string}
                />
              </FormControl>
              <Box>
                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                  <FormLabel m={0}>{t("Assessment Dimensions")}</FormLabel>
                  <Button size="sm" leftIcon={<FaPlus />} onClick={handleAddDimension}>
                    {t("Add Dimension")}
                  </Button>
                </Flex>
                {formData.dimensions.map((dimension, index) => (
                  <Box key={dimension.id} p={3} borderWidth="1px" borderRadius="md" mb={3}>
                    <Flex justifyContent="space-between" alignItems="center" mb={2}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">{t("Dimension Name")}</FormLabel>
                        <Input
                          size="sm"
                          value={dimension.name}
                          onChange={(e) => handleDimensionChange(index, "name", e.target.value)}
                        />
                      </FormControl>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        leftIcon={<FaTrash />}
                        onClick={() => handleRemoveDimension(index)}
                        ml={2}
                        mt={7}
                      />
                    </Flex>
                    <FormControl mt={2}>
                      <FormLabel fontSize="sm">{t("Description")}</FormLabel>
                      <Input
                        size="sm"
                        value={dimension.description}
                        onChange={(e) => handleDimensionChange(index, "description", e.target.value)}
                      />
                    </FormControl>
                  </Box>
                ))}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isCreating}>
              {isEditing ? t("Update") : t("Create")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
